import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, ChevronDown, Menu, X } from 'lucide-react';
import { Container } from './Container';
import { AudienceModal } from './AudienceModal';
import { AudienceDropdown } from './AudienceDropdown';
import { Button } from '../ui/button';
import { ROUTES } from '@shared/constants/routes';
import { AUDIENCE_NAV } from '../../config/audience-nav';
import {
  getRelevantToolsForAudience,
  getToolHref,
  type ToolDefinition,
} from '@shared/constants/tools-catalog';
import { getIcon } from '../../lib/lucide-icons';
import { useAudienceStore } from '../../stores/audience';
import { useEffectiveAudience } from '../../hooks/useEffectiveAudience';
import { cn } from '../../lib/cn';
// NOTE: l'ancien bandeau promotionnel ("Acteur intégré · 3 pôles · Diagnostic 60 sec")
// a été retiré pour alléger l'empilement vertical et libérer le focus sur le profil.

const TOOL_ICON_BG = {
  accent: 'bg-accent-50 text-accent-700 ring-accent-200/50',
  brand: 'bg-brand-50 text-brand-700 ring-brand-200/50',
  // lavender et sage sont @deprecated (alias brand/stone) — on garde le mapping pour
  // ne pas casser les outils qui utilisent encore ces tones, mais on rend la palette canonique.
  lavender: 'bg-brand-50 text-brand-700 ring-brand-200/50',
  sage: 'bg-stone-100 text-stone-700 ring-stone-200/60',
} as const;

const PRIMARY_LINKS = [
  { label: 'Cas clients', href: ROUTES.casClients },
  { label: 'Blog', href: ROUTES.blog },
  { label: 'À propos', href: ROUTES.aPropos },
];

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  /**
   * Menu desktop actif (un seul ouvert à la fois) :
   * - 'tools' pour le dropdown Outils
   * - <quickLink.label> pour les items contextuels avec children
   * - null si tout est fermé
   */
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  /** Accordéon mobile ouvert (label du quickLink avec children). */
  const [openMobileAccordion, setOpenMobileAccordion] = useState<string | null>(null);
  const [currentPath, setCurrentPath] = useState('');
  /** Ref vers la nav desktop pour détecter click-outside des mega-menus. */
  const navRef = useRef<HTMLElement | null>(null);
  /** Ref vers la racine du panneau mobile (pour focus restore sur close). */
  const mobileButtonRef = useRef<HTMLButtonElement | null>(null);
  const mobileMenuRef = useRef<HTMLDivElement | null>(null);

  const storedAudienceId = useAudienceStore((s) => s.audienceId);
  const setAudience = useAudienceStore((s) => s.setAudience);

  // ───────────── Résolution du profil (hook unifié) ─────────────
  // fromUrl  : audience détectée depuis l'URL uniquement
  // effective : URL > store > null (source de vérité pour la personnalisation UI)
  const { fromUrl: activeAudience, effective: effectiveAudience } = useEffectiveAudience();

  useEffect(() => {
    setCurrentPath(window.location.pathname);
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    // Vike déclenche 'vike:navigate' après chaque navigation SPA
    const onNavigate = () => setCurrentPath(window.location.pathname);
    window.addEventListener('vike:navigate', onNavigate);
    window.addEventListener('popstate', onNavigate);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('vike:navigate', onNavigate);
      window.removeEventListener('popstate', onNavigate);
    };
  }, []);

  // ───────────── Accessibilité : Escape ferme tout, click-outside ferme dropdowns ──
  useEffect(() => {
    if (!openMenu && !open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (openMenu) {
        setOpenMenu(null);
        // Rendre la fermeture clavier silencieuse : pas de scroll, pas de refocus particulier.
      }
      if (open) {
        setOpen(false);
        // Renvoyer le focus sur le bouton hamburger pour ne pas perdre l'utilisateur.
        mobileButtonRef.current?.focus();
      }
    };
    const onClickOutside = (e: MouseEvent) => {
      if (!openMenu) return;
      const target = e.target as Node | null;
      if (target && navRef.current && !navRef.current.contains(target)) {
        setOpenMenu(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('mousedown', onClickOutside);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('mousedown', onClickOutside);
    };
  }, [openMenu, open]);

  // Bloquer le scroll du body quand le menu mobile est ouvert (UX standard).
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Focus trap : le menu mobile est un dialog modal (aria-modal) — Tab doit
  // boucler entre le bouton hamburger et les éléments du menu, sans s'échapper
  // vers le document scroll-locké en dessous.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const menu = mobileMenuRef.current;
      const trigger = mobileButtonRef.current;
      if (!menu) return;
      const focusables: HTMLElement[] = [
        ...(trigger ? [trigger] : []),
        ...Array.from(
          menu.querySelectorAll<HTMLElement>(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
          ),
        ),
      ];
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      } else if (active && active !== trigger && !menu.contains(active)) {
        // Le focus s'est échappé du dialog → on le ramène au début.
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  /**
   * Helper : sur blur du container dropdown, si le focus part ailleurs que dans
   * un des enfants, on ferme le menu. Nécessaire pour la navigation clavier
   * (Tab vers la suite du document doit fermer le mega-menu).
   */
  const handleDropdownBlur = (e: React.FocusEvent<HTMLDivElement>, menuKey: string) => {
    const next = e.relatedTarget as Node | null;
    if (!next || !e.currentTarget.contains(next)) {
      setOpenMenu((prev) => (prev === menuKey ? null : prev));
    }
  };

  // ───────────── Auto-tracking : enregistrer le profil en cookie ─────────────
  // Quand l'utilisateur visite directement /particuliers, /artisans, etc.
  // (lien partagé, bookmark, SEO), on mémorise automatiquement son profil
  // dans le store + cookie sef_audience (90 j) sans action manuelle.
  useEffect(() => {
    if (activeAudience && activeAudience.id !== storedAudienceId) {
      setAudience(activeAudience.id);
    }
  }, [activeAudience, storedAudienceId, setAudience]);

  // Nav contextuelle : utilise le profil effectif (URL > store) pour
  // personnaliser les quick-links et le CTA sur TOUTES les pages, pas
  // seulement les pages d'audience directes.
  const audienceNav = effectiveAudience ? AUDIENCE_NAV[effectiveAudience.id] : null;

  // CTA principal : profil actif → CTA audience ; sinon hub outils (évite
  // de pousser le diagnostic CEE B2B aux anonymes / particuliers).
  const primaryCta = audienceNav
    ? { label: audienceNav.primaryCta.label, href: audienceNav.primaryCta.href }
    : { label: 'Voir nos outils', href: ROUTES.outils };
  const primaryCtaVariant = effectiveAudience?.id === 'artisans' ? 'accent' : 'primary';

  // ───────────── Outils filtrés par profil ─────────────
  // Priorité gérée par le hook : URL > store > null (tous les outils).
  const effectiveAudienceId = effectiveAudience?.id ?? null;

  const filteredTools = useMemo(
    () => getRelevantToolsForAudience(effectiveAudienceId),
    [effectiveAudienceId],
  );

  // Partition Recommandés / Autres pour guider l'œil quand un profil est actif.
  // `filteredTools` est déjà trié (recommandés en tête) par le catalogue.
  const recommendedTools = useMemo(
    () =>
      effectiveAudienceId
        ? filteredTools.filter((t) => t.priority.includes(effectiveAudienceId))
        : [],
    [filteredTools, effectiveAudienceId],
  );
  const otherTools = useMemo(
    () =>
      recommendedTools.length > 0 && effectiveAudienceId
        ? filteredTools.filter((t) => !t.priority.includes(effectiveAudienceId))
        : filteredTools,
    [filteredTools, recommendedTools, effectiveAudienceId],
  );

  // Rendu d'un lien outil (réutilisé desktop : sections Recommandés + Autres).
  const renderToolLink = (tool: ToolDefinition) => {
    const Icon = getIcon(tool.iconName);
    const isRecommended =
      effectiveAudienceId != null && tool.priority.includes(effectiveAudienceId);
    const toolHref = getToolHref(tool.id, effectiveAudienceId);
    return (
      <a
        key={toolHref + tool.id}
        href={toolHref}
        aria-current={currentPath === toolHref ? 'page' : undefined}
        className="group/t flex items-start gap-2.5 rounded-lg p-2.5 transition-colors duration-150 ease-out hover:bg-stone-50"
      >
        <span
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-md ring-1 ring-inset transition-transform duration-150 ease-out group-hover/t:scale-105',
            TOOL_ICON_BG[tool.tone],
          )}
        >
          <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <p className="text-[13px] font-semibold text-ink">{tool.title}</p>
            {isRecommended ? (
              <span className="rounded-md bg-brand-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-brand-700">
                Recommandé
              </span>
            ) : tool.badge ? (
              <span className="rounded-md bg-accent-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-accent-800">
                {tool.badge}
              </span>
            ) : null}
          </div>
          <p className="mt-0.5 text-[11px] text-stone-500">{tool.eyebrow}</p>
        </div>
        <ArrowRight
          className="mt-1 h-3 w-3 shrink-0 text-stone-300 transition-all duration-150 ease-out group-hover/t:translate-x-0.5 group-hover/t:text-stone-600"
          strokeWidth={2.2}
        />
      </a>
    );
  };

  return (
    <>
      <AudienceModal />

      {/* ───────────── HEADER UNIFIÉ ─────────────
          Une seule "carte" header (pas de border-b sur le main row + sub row :
          tout vit dans un même contenant glass, plus de "double header"). */}
      <header
        className={cn(
          'sticky top-0 z-40 transition-all duration-200 ease-out',
          scrolled
            ? 'border-b border-stone-200/70 bg-paper/85 shadow-[0_4px_24px_-8px_rgba(10,10,10,0.04)] backdrop-blur-xl backdrop-saturate-150'
            : 'border-b border-transparent bg-paper/0',
        )}
        style={{
          backdropFilter: scrolled ? 'blur(24px) saturate(180%)' : 'none',
          WebkitBackdropFilter: scrolled ? 'blur(24px) saturate(180%)' : 'none',
        }}
      >
        <Container className="flex h-14 items-center justify-between gap-3 md:h-16">
          {/* ───── Cluster GAUCHE : Logo seul ─────
              L'indicateur d'audience active est désormais porté par le
              dropdown "Mon espace [audience] ▾" au centre (label + icône
              colorée), évitant la duplication chip + bouton. */}
          <div className="flex min-w-0 items-center">
            <a
              href="/"
              aria-label="Solution Éco France — accueil"
              className="group inline-flex min-w-0 items-center gap-2"
            >
              <img
                src="/sef-symbol.svg"
                alt=""
                aria-hidden
                width={28}
                height={31}
                className="h-7 w-auto shrink-0 transition-transform duration-200 ease-out group-hover:scale-105"
              />
              <span className="truncate bg-gradient-to-br from-accent-800 via-accent-700 to-accent-600 bg-clip-text font-logo text-[12.5px] font-bold uppercase tracking-[-0.01em] text-transparent sm:text-[14px]">
                SOLUTION ÉCO FRANCE
              </span>
            </a>
          </div>

          {/* ───── Cluster CENTRE : Nav globale ─────
              ▸ Hors audience : Outils ▾ + liens secondaires
              ▸ Sur audience  : raccourcis contextuels (avec sous-menus
                pour Travaux / Aides) + Outils ▾
              Le SEUL switcher de profil est l'AudienceDropdown à droite. */}
          <nav
            ref={navRef}
            aria-label="Navigation principale"
            className="hidden min-w-0 flex-1 items-center justify-center gap-1 lg:flex"
          >
            {/* Raccourcis contextuels — chaque item est soit un lien direct,
                soit un dropdown (si `children` est défini dans audience-nav). */}
            {effectiveAudience && audienceNav && (
              <>
                {audienceNav.quickLinks.map((item) => {
                  const Icon = getIcon(item.iconName);
                  const hasChildren = !!item.children?.length;
                  const hasMegaMenu = !!item.megaMenu?.length;
                  const hasSubmenu = hasChildren || hasMegaMenu;
                  const isActive =
                    item.href === currentPath ||
                    (item.href.split('?')[0] === currentPath &&
                      currentPath !== effectiveAudience.href);

                  // ── Lien direct (pas de sous-menu)
                  if (!hasSubmenu) {
                    return (
                      <a
                        key={item.label}
                        href={item.href}
                        aria-current={isActive ? 'page' : undefined}
                        className={cn(
                          'group/ql inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out xl:px-3',
                          isActive
                            ? 'bg-stone-100 text-ink'
                            : 'text-stone-700 hover:bg-stone-100 hover:text-ink',
                        )}
                      >
                        <Icon className="hidden h-3.5 w-3.5 shrink-0 xl:block" strokeWidth={1.7} />
                        {item.label}
                        {item.badge && (
                          <span className="rounded bg-accent-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-accent-800">
                            {item.badge}
                          </span>
                        )}
                      </a>
                    );
                  }

                  // ── Item avec sous-menu (dropdown hover)
                  const menuKey = `nav:${item.label}`;
                  const isOpen = openMenu === menuKey;
                  const megaColCount = item.megaMenu?.length ?? 0;
                  const megaWidthClass =
                    megaColCount === 1
                      ? 'w-[340px]'
                      : megaColCount === 2
                        ? 'w-[480px]'
                        : 'w-[720px]';
                  const megaGridClass =
                    megaColCount === 1
                      ? 'grid-cols-1'
                      : megaColCount === 2
                        ? 'grid-cols-2'
                        : 'grid-cols-3';
                  const menuId = `nav-menu-${item.label.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`;
                  return (
                    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                    <div
                      key={item.label}
                      className="relative"
                      onMouseEnter={() => setOpenMenu(menuKey)}
                      onMouseLeave={() => setOpenMenu((prev) => (prev === menuKey ? null : prev))}
                      onBlur={(e) => handleDropdownBlur(e, menuKey)}
                    >
                      <button
                        type="button"
                        onClick={() => setOpenMenu(isOpen ? null : menuKey)}
                        className={cn(
                          'inline-flex cursor-pointer items-center gap-1.5 rounded-md px-2 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 xl:px-3',
                          isOpen || isActive
                            ? 'bg-stone-100 text-ink'
                            : 'text-stone-700 hover:bg-stone-100 hover:text-ink',
                        )}
                        aria-expanded={isOpen}
                        aria-controls={menuId}
                      >
                        <Icon className="hidden h-3.5 w-3.5 shrink-0 xl:block" strokeWidth={1.7} />
                        {item.label}
                        {item.badge && (
                          <span className="rounded bg-accent-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-accent-800">
                            {item.badge}
                          </span>
                        )}
                        <ChevronDown
                          className={cn(
                            'h-3 w-3 transition-transform duration-150 ease-out',
                            isOpen && 'rotate-180',
                          )}
                          strokeWidth={2}
                        />
                      </button>
                      {isOpen && hasMegaMenu && (
                        // ── MEGA-MENU adaptatif (2 ou 3 colonnes selon megaMenu.length).
                        // Aligné à gauche du bouton parent pour éviter le débordement viewport.
                        <div
                          id={menuId}
                          role="group"
                          aria-label={`Sous-menu ${item.label}`}
                          className={cn(
                            'absolute left-0 top-full z-50 mt-1 max-w-[calc(100vw-2rem)] pt-1',
                            megaWidthClass,
                          )}
                        >
                          <div className="overflow-hidden rounded-2xl border border-stone-200/70 bg-paper p-6 shadow-floating">
                            <div className={cn('grid gap-x-6 gap-y-4', megaGridClass)}>
                              {item.megaMenu!.map((group, groupIdx) => (
                                <div key={group.title || `group-${groupIdx}`} className="min-w-0">
                                  {group.title && (
                                    <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.16em] text-brand-700">
                                      {group.title}
                                    </p>
                                  )}
                                  <ul className="space-y-1">
                                    {group.items.map((child) => {
                                      const ChildIcon = getIcon(child.iconName);
                                      return (
                                        <li key={child.href + child.label}>
                                          <a
                                            href={child.href}
                                            aria-current={
                                              currentPath === child.href ? 'page' : undefined
                                            }
                                            className="group/c flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-[13px] font-medium text-stone-700 transition-colors duration-150 ease-out hover:bg-stone-50 hover:text-ink"
                                          >
                                            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-200/50">
                                              <ChildIcon className="h-3 w-3" strokeWidth={1.8} />
                                            </span>
                                            <span className="min-w-0 flex-1 truncate">
                                              {child.label}
                                            </span>
                                            {child.badge && (
                                              <span className="rounded bg-accent-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-accent-800">
                                                {child.badge}
                                              </span>
                                            )}
                                          </a>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </div>
                              ))}
                            </div>
                            {/* Footer mega-menu : lien vers le hub */}
                            <div className="-mx-2 mt-5 flex items-center justify-between border-t border-stone-100 px-2 pt-3">
                              <p className="text-[11px] text-stone-500">{item.description}</p>
                              <a
                                href={item.href}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-700 transition-colors duration-150 ease-out hover:text-brand-800"
                              >
                                Voir la page {item.label.toLowerCase()}
                                <ArrowRight className="h-2.5 w-2.5" strokeWidth={2.4} />
                              </a>
                            </div>
                          </div>
                        </div>
                      )}
                      {isOpen && !hasMegaMenu && item.children?.length && (
                        // ── Dropdown LISTE 1 colonne (fallback legacy)
                        <div
                          id={menuId}
                          role="group"
                          aria-label={`Sous-menu ${item.label}`}
                          className="absolute left-1/2 top-full z-50 mt-1 w-[420px] -translate-x-1/2 pt-1"
                        >
                          <div className="overflow-hidden rounded-xl border border-stone-200/70 bg-surface/95 p-2 shadow-floating backdrop-blur-xl">
                            {item.children.map((child) => {
                              const ChildIcon = getIcon(child.iconName);
                              return (
                                <a
                                  key={child.href + child.label}
                                  href={child.href}
                                  aria-current={currentPath === child.href ? 'page' : undefined}
                                  className="group/c flex items-start gap-2.5 rounded-lg p-2.5 transition-colors duration-150 ease-out hover:bg-stone-50"
                                >
                                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-200/50 transition-transform duration-150 ease-out group-hover/c:scale-105">
                                    <ChildIcon className="h-3.5 w-3.5" strokeWidth={1.8} />
                                  </span>
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5">
                                      <p className="text-[13px] font-semibold text-ink">
                                        {child.label}
                                      </p>
                                      {child.badge && (
                                        <span className="rounded-md bg-accent-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-accent-800">
                                          {child.badge}
                                        </span>
                                      )}
                                    </div>
                                    {child.description && (
                                      <p className="mt-0.5 line-clamp-1 text-[11px] text-stone-500">
                                        {child.description}
                                      </p>
                                    )}
                                  </div>
                                  <ArrowRight
                                    className="mt-1 h-3 w-3 shrink-0 text-stone-300 transition-all duration-150 ease-out group-hover/c:translate-x-0.5 group-hover/c:text-stone-600"
                                    strokeWidth={2.2}
                                  />
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                <span aria-hidden className="mx-1 h-4 w-px bg-stone-200" />
              </>
            )}

            {/* Dropdown Outils — filtré selon le profil actif */}
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div
              className="relative"
              onMouseEnter={() => setOpenMenu('tools')}
              onMouseLeave={() => setOpenMenu((prev) => (prev === 'tools' ? null : prev))}
              onBlur={(e) => handleDropdownBlur(e, 'tools')}
            >
              <button
                type="button"
                onClick={() => setOpenMenu((v) => (v === 'tools' ? null : 'tools'))}
                className={cn(
                  'inline-flex cursor-pointer items-center gap-1 rounded-md px-2.5 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2',
                  openMenu === 'tools'
                    ? 'bg-stone-100 text-ink'
                    : 'text-stone-700 hover:bg-stone-100 hover:text-ink',
                )}
                aria-expanded={openMenu === 'tools'}
                aria-controls="nav-menu-outils"
              >
                Outils
                {effectiveAudienceId && (
                  <span className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-brand-100 text-[9px] font-bold text-brand-700">
                    {filteredTools.length}
                  </span>
                )}
                <ChevronDown
                  className={cn(
                    'h-3 w-3 transition-transform duration-150 ease-out',
                    openMenu === 'tools' && 'rotate-180',
                  )}
                  strokeWidth={2}
                />
              </button>
              {openMenu === 'tools' && (
                <div
                  id="nav-menu-outils"
                  role="group"
                  aria-label="Sous-menu Outils"
                  className="absolute left-1/2 top-full z-50 mt-1 w-[440px] -translate-x-1/2 pt-1"
                >
                  <div className="overflow-hidden rounded-xl border border-stone-200/70 bg-surface/95 p-2 shadow-floating backdrop-blur-xl">
                    {recommendedTools.length > 0 && (
                      <>
                        <p className="px-2.5 pb-1 pt-1 text-[9px] font-bold uppercase tracking-[0.16em] text-brand-700">
                          Recommandés pour vous
                        </p>
                        {recommendedTools.map(renderToolLink)}
                        <p className="px-2.5 pb-1 pt-2.5 text-[9px] font-bold uppercase tracking-[0.16em] text-stone-400">
                          Autres outils
                        </p>
                      </>
                    )}
                    {otherTools.map(renderToolLink)}
                    <div className="mt-1 border-t border-stone-200/70 px-3 py-2">
                      <a
                        href={ROUTES.outils}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-700 hover:text-brand-800"
                      >
                        Voir tous les outils
                        <ArrowRight className="h-2.5 w-2.5" strokeWidth={2.4} />
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Liens secondaires : masqués dès qu'un profil est actif (URL ou store)
                pour éviter la surcharge visuelle ; accessibles via footer ou menu mobile */}
            {!effectiveAudience &&
              PRIMARY_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  aria-current={currentPath === link.href ? 'page' : undefined}
                  className={cn(
                    'group/link relative rounded-md px-2.5 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out',
                    currentPath === link.href
                      ? 'text-ink'
                      : 'text-stone-700 hover:bg-stone-100 hover:text-ink',
                  )}
                >
                  {link.label}
                  <span
                    aria-hidden
                    className={cn(
                      'absolute inset-x-2.5 -bottom-px h-px origin-left scale-x-0 bg-gradient-to-r from-brand-500 to-accent-500 transition-transform duration-200 ease-out group-hover/link:scale-x-100',
                      currentPath === link.href && 'scale-x-100',
                    )}
                  />
                </a>
              ))}
          </nav>

          {/* ───── Cluster DROITE : Switcher de profil + CTA contextuel ─────
              L'AudienceDropdown est l'UNIQUE point d'entrée pour switcher
              de profil. Il affiche aussi le profil actif (icône + label),
              donc plus besoin de chip à côté du logo. */}
          <div className="flex items-center gap-2">
            <div className="hidden md:flex">
              <AudienceDropdown />
            </div>

            <Button
              asChild
              size="sm"
              variant={primaryCtaVariant}
              className="group hidden shadow-cta-brand hover:shadow-cta-brand-hover md:inline-flex"
            >
              <a href={primaryCta.href}>
                <span className="relative mr-1 flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-glow-pulse rounded-full bg-accent-400/80" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent-400" />
                </span>
                {primaryCta.label}
                <ArrowRight className="h-3 w-3 transition-transform duration-150 ease-out group-hover/btn:translate-x-0.5" />
              </a>
            </Button>
            <button
              ref={mobileButtonRef}
              type="button"
              aria-label={open ? 'Fermer le menu' : 'Ouvrir le menu'}
              aria-expanded={open}
              aria-controls="mobile-menu"
              onClick={() => setOpen((v) => !v)}
              className="inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-lg border border-stone-200 bg-paper text-ink shadow-[0_1px_2px_rgba(10,10,10,.04)] transition-colors duration-150 ease-out hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 lg:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </Container>

        {/* ───────────── MOBILE MENU ───────────── */}
        {open && (
          <div
            id="mobile-menu"
            ref={mobileMenuRef}
            role="dialog"
            aria-label="Menu principal"
            aria-modal="true"
            className="border-t border-stone-200/70 bg-paper/95 backdrop-blur-xl lg:hidden"
          >
            <Container className="flex flex-col gap-1 py-4">
              {/* ── Sélecteur de profil unique du menu mobile (md:hidden car
                  la barre header ≥768px porte déjà son propre dropdown).
                  Pas de liste « Vous êtes ? » en doublon plus bas. ── */}
              <div className="mb-2 flex flex-col gap-2.5 rounded-xl border border-stone-200 bg-stone-50/80 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between md:hidden">
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-stone-400">
                    Mon profil
                  </p>
                  <p className="mt-0.5 text-[12px] font-medium text-stone-700">
                    Personnalisez la navigation et les outils
                  </p>
                </div>
                <div className="shrink-0 self-start sm:self-auto">
                  <AudienceDropdown />
                </div>
              </div>

              {/* Raccourcis profil actif (URL ou store) */}
              {effectiveAudience && (
                <>
                  <p className="px-3 pb-1.5 pt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-stone-400">
                    Vos raccourcis
                  </p>
                  {audienceNav?.quickLinks.map((item) => {
                    const Icon = getIcon(item.iconName);
                    const hasMega = !!item.megaMenu?.length;
                    const hasChildren = !!item.children?.length;

                    // ── Lien direct
                    if (!hasMega && !hasChildren) {
                      return (
                        <a
                          key={item.label}
                          href={item.href}
                          aria-current={currentPath === item.href ? 'page' : undefined}
                          onClick={() => setOpen(false)}
                          className="inline-flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-stone-800 transition-colors duration-150 ease-out hover:bg-stone-100"
                        >
                          <span className="inline-flex items-center gap-2.5">
                            <Icon className="h-4 w-4 shrink-0" strokeWidth={1.7} />
                            {item.label}
                          </span>
                          {item.badge && (
                            <span className="rounded bg-accent-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-accent-800">
                              {item.badge}
                            </span>
                          )}
                        </a>
                      );
                    }

                    // ── Accordéon (mega-menu OU children)
                    const isExpanded = openMobileAccordion === item.label;
                    return (
                      <div key={item.label} className="flex flex-col">
                        <button
                          type="button"
                          onClick={() =>
                            setOpenMobileAccordion((v) => (v === item.label ? null : item.label))
                          }
                          aria-expanded={isExpanded}
                          className={cn(
                            'inline-flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-stone-800 transition-colors duration-150 ease-out hover:bg-stone-100',
                            isExpanded && 'bg-stone-100',
                          )}
                        >
                          <span className="inline-flex items-center gap-2.5">
                            <Icon className="h-4 w-4 shrink-0" strokeWidth={1.7} />
                            {item.label}
                          </span>
                          <ChevronDown
                            className={cn(
                              'h-3.5 w-3.5 text-stone-400 transition-transform duration-150 ease-out',
                              isExpanded && 'rotate-180',
                            )}
                            strokeWidth={2}
                          />
                        </button>
                        {isExpanded && (
                          <div className="ml-2 mt-1 flex flex-col border-l border-stone-200 pl-3">
                            {hasMega
                              ? item.megaMenu!.map((group, groupIdx) => (
                                  <div
                                    key={group.title || `group-${groupIdx}`}
                                    className="mb-1.5 last:mb-0"
                                  >
                                    {group.title && (
                                      <p className="px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-[0.14em] text-brand-700">
                                        {group.title}
                                      </p>
                                    )}
                                    {group.items.map((child) => {
                                      const ChildIcon = getIcon(child.iconName);
                                      return (
                                        <a
                                          key={child.href + child.label}
                                          href={child.href}
                                          onClick={() => setOpen(false)}
                                          className="inline-flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-[13px] font-medium text-stone-700 transition-colors duration-150 ease-out hover:bg-stone-100"
                                        >
                                          <span className="inline-flex items-center gap-2.5">
                                            <ChildIcon
                                              className="h-3.5 w-3.5 shrink-0 text-stone-400"
                                              strokeWidth={1.7}
                                            />
                                            {child.label}
                                          </span>
                                          {child.badge && (
                                            <span className="rounded bg-accent-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-accent-800">
                                              {child.badge}
                                            </span>
                                          )}
                                        </a>
                                      );
                                    })}
                                  </div>
                                ))
                              : item.children!.map((child) => {
                                  const ChildIcon = getIcon(child.iconName);
                                  return (
                                    <a
                                      key={child.href + child.label}
                                      href={child.href}
                                      onClick={() => setOpen(false)}
                                      className="inline-flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-[13px] font-medium text-stone-700 transition-colors duration-150 ease-out hover:bg-stone-100"
                                    >
                                      <span className="inline-flex items-center gap-2.5">
                                        <ChildIcon
                                          className="h-3.5 w-3.5 shrink-0 text-stone-400"
                                          strokeWidth={1.7}
                                        />
                                        {child.label}
                                      </span>
                                      {child.badge && (
                                        <span className="rounded bg-accent-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-accent-800">
                                          {child.badge}
                                        </span>
                                      )}
                                    </a>
                                  );
                                })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}

              {/* (Liste « Vous êtes ? » retirée — le bloc « Mon profil »
                  en tête de menu est l'unique sélecteur de profil.) */}

              {/* Outils — filtrés selon le profil actif ou mémorisé */}
              <div className="flex items-center justify-between px-3 pb-1 pt-3">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-stone-400">
                  Outils
                  {effectiveAudienceId && (
                    <span className="ml-1.5 text-stone-300">({filteredTools.length})</span>
                  )}
                </p>
                <a
                  href={ROUTES.outils}
                  className="text-[11px] font-semibold text-brand-700 hover:text-brand-800"
                  onClick={() => setOpen(false)}
                >
                  Voir tous →
                </a>
              </div>
              {filteredTools.map((tool) => {
                const Icon = getIcon(tool.iconName);
                const toolHref = getToolHref(tool.id, effectiveAudienceId);
                const isRecommended =
                  effectiveAudienceId != null && tool.priority.includes(effectiveAudienceId);
                return (
                  <a
                    key={toolHref + tool.id}
                    href={toolHref}
                    className="inline-flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium text-stone-800 transition-colors duration-150 ease-out hover:bg-stone-100"
                    onClick={() => setOpen(false)}
                  >
                    <span
                      className={cn(
                        'flex h-7 w-7 shrink-0 items-center justify-center rounded-md ring-1 ring-inset',
                        TOOL_ICON_BG[tool.tone],
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
                    </span>
                    <span className="flex-1">{tool.title}</span>
                    {isRecommended && (
                      <span className="rounded bg-brand-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-brand-700">
                        Recommandé
                      </span>
                    )}
                  </a>
                );
              })}

              <p className="px-3 pb-1.5 pt-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-stone-400">
                Plus
              </p>
              {[...PRIMARY_LINKS, { label: 'Contact', href: ROUTES.contact }].map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  aria-current={currentPath === item.href ? 'page' : undefined}
                  className="rounded-lg px-3 py-2.5 text-sm font-medium text-stone-800 transition-colors duration-150 ease-out hover:bg-stone-100"
                  onClick={() => setOpen(false)}
                >
                  {item.label}
                </a>
              ))}

              <Button asChild size="lg" variant={primaryCtaVariant} className="mt-3 w-full">
                <a href={primaryCta.href} onClick={() => setOpen(false)}>
                  {primaryCta.label}
                  <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.2} />
                </a>
              </Button>
            </Container>
          </div>
        )}
      </header>
    </>
  );
}
